import React, { useState, useCallback, useEffect } from 'react';
import DialogContent from '@mui/material/DialogContent';
import TextFieldWithClear from 'components/TextFieldWithIcon/TextFieldWithIcon';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { FormGroup } from '@mui/material';
import BaseForm from 'components/Inputs/Forms/BaseForm';
import CustomSelect from 'components/Inputs/Select/Select';

const styles = require('../styles.pcss');

type InitialValues = {
  companyId?: string;
  companyIds?: string[];
  ignoreFilePrefix?: string[];
  filePrefix?: string;
  bucket?: string;
  provider?: string;
  allowMatchingByLast4?: boolean;
  status?: string;
};

type Props = {
  onSubmit: Function;
  editMode?: boolean;
  initialValues?: InitialValues;
}

export default function CorpCardIntegrationForm(props: Props) {
  const { editMode = false, initialValues = {} } = props;

  const [companyId, setCompanyId] = useState(initialValues.companyId ?? '');
  const [companyIds, setCompanyIds] = useState<string[]>(initialValues.companyIds ?? []);
  const [ignoreFilePrefix, setIgnoreFilePrefix] = useState<string[]>(initialValues.ignoreFilePrefix ?? []);
  const [filePrefix, setFilePrefix] = useState(initialValues.filePrefix ?? '');
  const [bucket, setBucket] = useState(initialValues.bucket ?? '');

  const [provider, setProvider] = useState(initialValues.provider ?? '');
  const [allowMatchingByLast4, setAllowMatchingByLast4] = useState(
    initialValues.allowMatchingByLast4 ?? false,
  );
  const [status, setStatus] = useState(initialValues.status ?? 'enabled');

  useEffect(() => {
    if (!initialValues) return;
    setCompanyId(initialValues.companyId ?? '');
    setCompanyIds(initialValues.companyIds ?? []);
    setIgnoreFilePrefix(initialValues.ignoreFilePrefix ?? []);
    setFilePrefix(initialValues.filePrefix ?? '');
    setBucket(initialValues.bucket ?? '');
    setProvider(initialValues.provider ?? '');
    setAllowMatchingByLast4(initialValues.allowMatchingByLast4 ?? false);
    setStatus(initialValues.status ?? 'enabled');
  }, [initialValues?.companyId, initialValues?.companyIds, initialValues?.ignoreFilePrefix, initialValues?.filePrefix, initialValues?.bucket, initialValues?.provider, initialValues?.allowMatchingByLast4, initialValues?.status]);

  const trimFilePrefix = (prefix: string, bucket: string) => {
    if (/^usbc/i.test(prefix)) {
      return prefix.slice(0, 14);
    }
    if (bucket === 'visa') {
      return prefix;
    }
    if (bucket === 'amex') {
      const match = prefix.match(/GL1025/);
      if (match) {
        return prefix.slice(0, match.index + match[0].length);
      } else {
        return prefix;
      }
    }
    if (bucket === 'mastercard' || bucket === 'mastercardfeed') {
      const match = prefix.match(/_/);
      if (match) {
        return prefix.slice(0, match.index);
      } else {
        return prefix;
      }
    }
    return prefix;
  };

  const validateThenSubmit = useCallback(function () {
    if (editMode) {
      const data: any = {
        filePrefix,
        bucket,
        provider,
        allowMatchingByLast4,
        companyIds,
        ignoreFilePrefix,
        status,
      };
      const missingFields = ['provider'].filter(key => !data[key]);
      if (missingFields.length) {
        props.onSubmit({ isValid: false, message: `Missing Field(s): ${missingFields.join(', ')}.` });
      } else {
        data.filePrefix = trimFilePrefix(filePrefix, bucket);
        if (/usb/i.test(data.filePrefix)) data.filePrefix = data.filePrefix.toLowerCase();
        props.onSubmit({ isValid: true, data });
      }
    } else {
      const data: any = { companyId, filePrefix, bucket, provider };
      const missingFields = ['companyId', 'provider'].filter(key => !data[key]);
      if (missingFields.length) {
        props.onSubmit({ isValid: false, message: `Missing Field(s): ${missingFields.join(', ')}.` });
      } else {
        data.filePrefix = trimFilePrefix(filePrefix, bucket);
        if (/usb/i.test(data.filePrefix)) data.filePrefix = data.filePrefix.toLowerCase();
        props.onSubmit({ isValid: true, data });
      }
    }
  }, [companyId, companyIds, ignoreFilePrefix, filePrefix, bucket, provider, allowMatchingByLast4, status, editMode]);



  return (
    <DialogContent>
      <div>Please make sure to have company id and provider on hand before you fill out this form.</div>
      <BaseForm onSubmit={validateThenSubmit}>
        {!editMode && (
          <TextFieldWithClear
            label="Company Id/Client Id"
            onChange={({ target: { value } }: any) => setCompanyId(value)}
            value={companyId}
            onClear={() => setCompanyId('')}
            margin="normal"
            variant="outlined"
          />
        )}
        <TextFieldWithClear
          label="File Prefix"
          onChange={({ target: { value } }: any) => setFilePrefix(value)}
          value={filePrefix}
          onClear={() => setFilePrefix('')}
          margin="normal"
          variant="outlined"
        />
        <TextFieldWithClear
          label="Bucket"
          onChange={({ target: { value } }: any) => setBucket(value)}
          value={bucket}
          onClear={() => setBucket('')}
          margin="normal"
          variant="outlined"
        />
        <TextFieldWithClear
          label="Provider"
          onChange={({ target: { value } }: any) => setProvider(value)}
          value={provider}
          onClear={() => setProvider('')}
          margin="normal"
          variant="outlined"
        />
        {editMode && (
          <>
            <CustomSelect
              menuItems={[
                { value: 'enabled', label: 'Enabled' },
                { value: 'disabled', label: 'Disabled' },
                { value: 'loginRequired', label: 'Login Required' },
                { value: 'pending', label: 'Pending' },
              ]}
              label="Status"
              onChange={({ target: { value } }: any) => setStatus(value)}
              value={status}
              margin="normal"
              variant="outlined"
              fullWidth
            />
            <FormControlLabel
              labelPlacement="start"
              label="Allow Matching By Last 4"
              className={styles.editFormControlLabel}
              control={
                <Switch
                  checked={allowMatchingByLast4}
                  onChange={(e) => setAllowMatchingByLast4(e.target.checked)}
                  color="primary"
                />
              }
            />
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={companyIds}
              onChange={(_, newValue: string[]) => setCompanyIds(newValue)}
              ChipProps={{ size: 'small', variant: 'outlined' }}
              sx={{ '& .MuiOutlinedInput-root': { height: 'fit-content' }, width: '100%' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Company IDs"
                  placeholder={companyIds.length === 0 ? 'Type and press Enter to add' : ''}
                  margin="normal"
                />
              )}
            />
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={ignoreFilePrefix}
              onChange={(_, newValue: string[]) => setIgnoreFilePrefix(newValue)}
              ChipProps={{ size: 'small', variant: 'outlined' }}
              sx={{ '& .MuiOutlinedInput-root': { height: 'fit-content' }, width: '100%' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Ignore File Prefix"
                  placeholder={ignoreFilePrefix.length === 0 ? 'Type and press Enter to add' : ''}
                  margin="normal"
                />
              )}
            />
          </>
        )}
      </BaseForm>
      <FormGroup />
    </DialogContent>
  );
}
